type DuplicateMauiAppOptions =
	| {
			/** Pre-formed container ARI for any product (e.g. `ari:cloud:jira:<cloudId>:issue/<issueId>`). */
			containerAri: string;
			shouldRetry?: boolean;
	  }
	| {
			containerAri?: undefined;
			/** Confluence page or blog post ID used to build a Confluence ARI. */
			containerContentId: string;
			/** Cloud ID used to build `ari:cloud:confluence:<cloudId>:<contentType>/<containerContentId>`. */
			cloudId?: string;
			/** Content type of the container ('page' | 'blogpost'). Defaults to 'page'. */
			contentType?: string;
			shouldRetry?: boolean;
	  };

/** Result shape returned by `duplicateMauiApp`. */
export type DuplicateMauiAppSuccess = {
	/** App ID to insert (new on a real duplicate, original when the BE no-ops). */
	id: string;
	/** `1` if first call succeeded, `2` if retry rescued it. */
	attempts: DuplicateMauiAppAttemptCount;
	statusCode?: number;
};

export type DuplicateMauiAppAttemptCount = 1 | 2;

type DuplicateMauiAppErrorAttributes = {
	attempts?: DuplicateMauiAppAttemptCount;
	code?: string;
	status?: number;
	statusCode?: number;
};

const GENERIC_FAILURE_MESSAGE = "We couldn't duplicate this component. Please try again.";
const MISSING_ID_MESSAGE =
	"We couldn't duplicate this component. The server did not return a new component ID.";

async function sendDuplicateRequest(
	url: string,
	body: string,
): Promise<{ id: string; statusCode: number }> {
	const response = await fetch(url, {
		method: 'POST',
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body,
	});

	if (!response.ok) {
		let errorBody: { error?: { code?: string; message?: string } } | undefined;
		try {
			errorBody = (await response.json()) as typeof errorBody;
		} catch {
			errorBody = undefined;
		}

		const fallbackMessage =
			response.status === 403
				? 'You do not have permission to duplicate this component.'
				: GENERIC_FAILURE_MESSAGE;
		const error = new Error(errorBody?.error?.message || fallbackMessage) as Error & {
			code?: string;
			status?: number;
			statusCode?: number;
		};
		error.code = errorBody?.error?.code;
		error.status = response.status;
		error.statusCode = response.status;
		throw error;
	}

	const data = await response.json().catch(() => undefined);
	if (!data?.id) {
		const error = new Error(MISSING_ID_MESSAGE) as Error & { statusCode?: number };
		error.statusCode = response.status;
		throw error;
	}

	return { id: data.id, statusCode: response.status };
}

/**
 * Duplicates a MAUI app via the Remix duplicate API.
 *
 * Pass `{ containerAri }` for a pre-formed ARI, or `{ containerContentId, cloudId, contentType }` to build a Confluence ARI.
 *
 * 403 permission errors throw immediately; other failures retry once.
 * BE no-ops when the app is already attached (returns existing id), so callers don't need an FE-side skip guard.
 */
export async function duplicateMauiApp(
	appId: string,
	options: DuplicateMauiAppOptions,
): Promise<DuplicateMauiAppSuccess> {
	let containerAri: string | undefined;
	if ('containerAri' in options) {
		containerAri = options.containerAri;
	} else {
		const ariContentType = options.contentType === 'blogpost' ? 'blogpost' : 'page';
		containerAri = options.cloudId
			? `ari:cloud:confluence:${options.cloudId}:${ariContentType}/${options.containerContentId}`
			: options.containerContentId;
	}

	const url = `/gateway/api/remix/api/v1/ui/${encodeURIComponent(appId)}/duplicate`;
	const body = JSON.stringify({
		containerAri,
	});
	const attempts = options.shouldRetry === false ? 2 : 1;

	try {
		const { id, statusCode } = await sendDuplicateRequest(url, body);
		return { id, statusCode, attempts };
	} catch (error) {
		if (typeof error === 'object' && error !== null) {
			(error as DuplicateMauiAppErrorAttributes).attempts = attempts;
		}

		const statusCode = (error as DuplicateMauiAppErrorAttributes | null)?.statusCode;
		if (statusCode !== 403 && options.shouldRetry !== false) {
			return duplicateMauiApp(appId, { ...options, shouldRetry: false });
		}

		throw error;
	}
}
